import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PaymentSuccess = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [planName, setPlanName] = useState('');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;
  const pollInterval = 2000;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await fetch(`${API}/payments/status/${sessionId}`);
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          setStatus('success');
          setPlanName(data.plan === 'starter' ? 'Starter' : 'Pro');
          
          // Update local user data
          if (user) {
            const updatedUser = { ...user, plan: data.plan };
            localStorage.setItem('user', JSON.stringify(updatedUser));
          }
          return;
        } else if (data.status === 'expired') {
          setStatus('failed');
          return;
        }
        
        // Continue polling
        if (attempts < maxAttempts) {
          setAttempts(prev => prev + 1);
          setTimeout(checkPaymentStatus, pollInterval);
        } else {
          setStatus('failed');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < maxAttempts) {
          setAttempts(prev => prev + 1);
          setTimeout(checkPaymentStatus, pollInterval);
        } else {
          setStatus('failed');
        }
      }
    };

    checkPaymentStatus();
  }, [searchParams, attempts, user]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-orange-600 animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment...</h1>
            <p className="text-gray-600">Please wait while we confirm your payment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome to {planName}!</h1>
            <p className="text-gray-600 mb-6">
              Your payment was successful. You now have access to all {planName} features.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/content-strategy')} 
                className="w-full bg-orange-600 hover:bg-orange-700 text-white"
              >
                Start Creating Content →
              </Button>
              <Button 
                onClick={() => navigate('/dashboard')} 
                variant="outline"
                className="w-full"
              >
                Go to Dashboard
              </Button>
            </div>
          </>
        )}

        {status === 'failed' && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Issue</h1>
            <p className="text-gray-600 mb-6">
              We couldn't confirm your payment. Please try again or contact support.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/#pricing')} 
                className="w-full bg-orange-600 hover:bg-orange-700 text-white"
              >
                Try Again
              </Button>
              <Button 
                onClick={() => navigate('/')} 
                variant="outline"
                className="w-full"
              >
                Back to Home
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
